import Hero from "./Hero";

export default function App() {
  return (
    <div className="bg-black text-white">
      {/* Scroll-driven hero section */}
      <Hero />

      {/* Content Section 1 */}
      <section className="min-h-screen bg-gradient-to-b from-black via-slate-900 to-black px-8 py-20 flex items-center justify-center">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-bold mb-8 text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
            Premium Scroll Experience
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Scroll-driven animations that are smooth, performant, and visually stunning. Built with GSAP ScrollTrigger and React for the best user experience.
          </p>
          <div className="h-1 w-24 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto" />
        </div>
      </section>

      {/* Features Section */}
      <section className="min-h-screen bg-black px-8 py-20 relative overflow-hidden">
        {/* Animated background orbs */}
        <div className="absolute top-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">Key Features</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">Built with cutting-edge technology for unparalleled performance</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group p-8 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm hover:from-blue-900/30 hover:to-purple-900/30 transition-all duration-500 border border-slate-700 hover:border-blue-500/50 relative overflow-hidden cursor-pointer">
              {/* Shimmer effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
              </div>
              
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl mb-6 flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg shadow-blue-500/50">
                  <span className="text-3xl">⚡</span>
                </div>
                <h3 className="text-2xl font-bold mb-4 group-hover:text-blue-400 transition-colors">Smooth Performance</h3>
                <p className="text-gray-300 leading-relaxed group-hover:text-gray-200 transition-colors">Optimized animations using GPU-accelerated transform properties for buttery-smooth 60fps performance on all devices.</p>
              </div>
            </div>

            <div className="group p-8 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm hover:from-purple-900/30 hover:to-pink-900/30 transition-all duration-500 border border-slate-700 hover:border-purple-500/50 relative overflow-hidden cursor-pointer">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-500/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
              </div>
              
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl mb-6 flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg shadow-purple-500/50">
                  <span className="text-3xl">🎨</span>
                </div>
                <h3 className="text-2xl font-bold mb-4 group-hover:text-purple-400 transition-colors">Premium Design</h3>
                <p className="text-gray-300 leading-relaxed group-hover:text-gray-200 transition-colors">Modern UI with vibrant gradients, glassmorphism effects, and carefully crafted visual hierarchy for maximum impact.</p>
              </div>
            </div>

            <div className="group p-8 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm hover:from-pink-900/30 hover:to-red-900/30 transition-all duration-500 border border-slate-700 hover:border-pink-500/50 relative overflow-hidden cursor-pointer">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-pink-500/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
              </div>
              
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl mb-6 flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg shadow-pink-500/50">
                  <span className="text-3xl">📱</span>
                </div>
                <h3 className="text-2xl font-bold mb-4 group-hover:text-pink-400 transition-colors">Fully Responsive</h3>
                <p className="text-gray-300 leading-relaxed group-hover:text-gray-200 transition-colors">Perfect experience on mobile, tablet, and desktop. Built with Tailwind CSS for seamless responsive design.</p>
              </div>
            </div>
          </div>
          
          {/* Tech Stack badges */}
          <div className="mt-20 text-center">
            <p className="text-gray-400 text-sm mb-6 tracking-wider">POWERED BY</p>
            <div className="flex flex-wrap justify-center gap-4">
              {['React.js', 'GSAP', 'Tailwind CSS', 'ScrollTrigger', 'JavaScript ES6+', 'CSS3'].map((tech, i) => (
                <div key={tech} className="px-6 py-3 rounded-full bg-slate-800/50 border border-slate-700 hover:border-blue-500/50 transition-all backdrop-blur-sm hover:scale-105 cursor-pointer" style={{ animationDelay: `${i * 0.1}s` }}>
                  <p className="font-medium text-sm text-gray-300">{tech}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-12 px-8 text-center border-t border-slate-800">
        <p className="text-gray-400">Scroll-Driven Hero Animation • Built with React and GSAP</p>
      </footer>
    </div>
  );
}